import * as React from 'react';
import { forwardRef } from 'react';
import isMacOs from '../../utils/isMacOs';
import { Combobox } from '@headlessui/react';
import useFocusRefOnKeyPress from '../../hooks/useFocusRefOnKeyPress';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import useFetchSearchGeneralAndNavToResult from '../../hooks/useFetchSearchGeneralAndNavToResult';
import AiSearchIcon from '../../../public/images/icons/ai-search.svg';
import { useSearch } from '../../contexts/SearchContext';
import { Place } from '@th/shared/src/types/Place';
import { PropertySnapshot } from '@th/shared/src/types/PropertySnapshot';
import SvgIcon from '../icons/SvgIcon';

type Props = {
  searchPhrase: string;
  onChangeSearchPhrase: (searchText: string) => void;
  onChangeSelectedItem: (item: any) => void;
  showKeyboardShortcut?: boolean;
  placeholder?: string;
  displayValueFormatter: (item: Place | PropertySnapshot | string) => string;
  activeOption: any;
};

const PropertySearchBar = forwardRef<HTMLInputElement, Props>(
  (
    {
      searchPhrase,
      onChangeSearchPhrase,
      onChangeSelectedItem,
      showKeyboardShortcut = true,
      placeholder = 'Address, Street, Postal Code, MLS',
      displayValueFormatter,
      activeOption,
    },
    ref,
  ) => {
    useFocusRefOnKeyPress(ref);

    const { clearGeoFilters } = useSearch();
    const { isDesktop, isMobile } = useWindowDimensions();
    const [fetchSearchGeneralAndNavToResult] =
      useFetchSearchGeneralAndNavToResult();

    return (
      <div>
        <label htmlFor="search" className="sr-only">
          Search
        </label>
        <div className="relative rounded-full shadow-sm">
          <Combobox.Button as={'div'}>
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <div className="h-5 w-5 text-gray-400">
                <AiSearchIcon />
              </div>
            </div>
            <Combobox.Input
              // value={DO NOT USE VALUE HERE, IT WILL BREAK THE COMBOBOX!}
              ref={ref}
              autoComplete="off"
              className="block w-full rounded-full border-gray-300 pl-10 sm:text-sm hover:bg-gray-50 pr-14 placeholder:text-gray-400"
              placeholder={placeholder}
              onChange={event => onChangeSearchPhrase(event.target.value)}
              onBlur={() => onChangeSearchPhrase('')}
              displayValue={item => {
                if (item) {
                  return displayValueFormatter(
                    item as Place | PropertySnapshot | string,
                  );
                } else {
                  return '';
                }
              }}
              onKeyDown={async event => {
                if (event.key === 'Enter') {
                  const isUserFocusedOnAnOption = Boolean(activeOption);
                  if (isUserFocusedOnAnOption) return;
                  await fetchSearchGeneralAndNavToResult(searchPhrase);
                }
              }}
            />
            {showKeyboardShortcut && isDesktop && (
              <div className="absolute inset-y-0 right-0 flex py-1.5 pr-3">
                <kbd
                  className="inline-flex items-center rounded border border-gray-200 px-2 font-sans text-sm font-medium text-gray-400"
                  suppressHydrationWarning={true}
                >
                  {isMacOs() ? '⌘' : 'Ctrl+'}K
                </kbd>
              </div>
            )}
            {isMobile && (
              <div className="absolute inset-y-0 right-0 flex py-1.5 pr-3">
                <button
                  className="inline-flex items-center"
                  onClick={() => {
                    onChangeSelectedItem(null);
                    onChangeSearchPhrase('');
                    clearGeoFilters();
                  }}
                >
                  <SvgIcon name={'HiX'} className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            )}
          </Combobox.Button>
        </div>
      </div>
    );
  },
);

PropertySearchBar.displayName = 'PropertySearchBar';
export default PropertySearchBar;
